import React, { useState } from "react";
import ScrollToTop from "../scrollToTop";
import style from "./style.module.css";
import { CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import {
  FaDownload,
  FaLongArrowAltRight,
  FaPlus,
  FaShareAlt,
} from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import {
  checkCompletedImage,
  extractFacesMulti,
  updateMultiFaceImage,
} from "../../services/api/effects";
// import { DecryptImage } from "../../constants/DecryptImage";
import DownloadDetailSection from "../downloadDetailSection";
import Review from "../Review";
import InstallSection from "../installSection";
import Footer from "../Footer";
import CustomProgressBar from "../CustomProgressBar";
import toastFile from "../../services/toastFile";
import { AutoAwesome, PhotoCamera } from "@mui/icons-material";

export interface MultiFaceSwapTemplateProps {
  initialSwappedImage: string;
  toastPrefix: string;
  badgeIcon: React.ReactNode;
  badgeText: string;
  heroHeading: string;
  heroSubheading: string;
  heroText: string;

  // Custom components
  HeroSectionComponent: React.ComponentType;
  FeaturesComponent: React.ComponentType;
  GuideComponent: React.ComponentType;
  WhoCanUseComponent: React.ComponentType;
  BenefitsComponent: React.ComponentType;
  FAQComponent: React.ComponentType;
}

const MultiFaceSwapTemplate: React.FC<MultiFaceSwapTemplateProps> = ({
  initialSwappedImage,
  toastPrefix,
  badgeIcon,
  badgeText,
  heroHeading,
  heroSubheading,
  heroText,
  HeroSectionComponent,
  FeaturesComponent,
  GuideComponent,
  WhoCanUseComponent,
  BenefitsComponent,
  FAQComponent,
}) => {
  const [progress, setProgress] = useState<number>(0);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [swapInProgress, setSwapInProgress] = useState<boolean>(false);
  const [dragOverOriginal, setDragOverOriginal] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [convertedImage, setConvertedImage] = useState<string | null>(null);
  const [isTapped, setIsTapped] = useState<boolean>(false);
  const [extractedFaces, setExtractedFaces] = useState<string[]>([]);
  const [uploadedFaces, setUploadedFaces] = useState<string[]>([]);
  const [dragOverFaces, setDragOverFaces] = useState<boolean[]>([]);

  const handleExtractFaces = async (imageBase64: string) => {
    let res = await extractFacesMulti({
      original_image: imageBase64.replace(/^data:image\/[a-z]+;base64,/, ""),
    });

    if (res?.data?.status === 200) {
      const faces = res.data.faces.map((face: string) => {
        // let decryptedFace = DecryptImage(face);
        return `data:image/jpeg;base64,${face}`;
      });
      setUploadedFaces([]);
      setExtractedFaces(faces);
      setDragOverFaces(Array(faces.length).fill(false));
    }
  };

  const fetchFSImageData = async (FSResp: string) => {
    let payload = {
      image_id: FSResp,
    };
    try {
      const res = await checkCompletedImage(payload);
      console.log({ res: res.data.data?.is_found });
      if (res?.data?.status === 400) {
        toastFile("error", `${toastPrefix} Face Swap Failed! Please try with another image.`);
        setSwapInProgress(false);
      } else if (res.data.data?.is_found) {
        let originalImg = "";

        try {
          // originalImg = DecryptImage(res?.data?.data?.img);
          originalImg = `data:image/jpeg;base64,${res?.data?.data?.img}`;

          const currentProgress = progress;
          const increment = (100 - currentProgress) / 20;
          let step = 0;

          const progressInterval = setInterval(() => {
            step++;

            setProgress((prev) => Math.min(prev + increment, 100));

            if (step >= 20) {
              clearInterval(progressInterval);

              setTimeout(() => {
                setConvertedImage(originalImg);
                setUploadedFaces([]);
                setSwapInProgress(false);

                toastFile("success", `${toastPrefix} Face Swap Completed Successfully!`);
              }, 100);
            }
          }, 100);
        } catch (e) {
          console.log(e);
          setSwapInProgress(false);
        }
      } else {
        setTimeout(() => {
          fetchFSImageData(FSResp);
        }, 3000);
      }
    } catch (error) {
      console.log("API error:", error);
      toastFile("error", `${toastPrefix} Face Swap Failed! Please try with another image.`);
      setSwapInProgress(false);
    }
  };

  const applyEffect = async () => {
    if (!originalImage || uploadedFaces.length !== extractedFaces.length) return;

    let payload = {
      original_image: originalImage.replace(/^data:image\/[a-z]+;base64,/, ""),
      faces: uploadedFaces.map((face) =>
        face.replace(/^data:image\/[a-z]+;base64,/, "")
      ),
    };

    try {
      const res = await updateMultiFaceImage(payload);
      if (res?.data?.status === 200) {
        fetchFSImageData(res?.data?.data?.id);
      } else {
        setSwapInProgress(false);
      }
    } catch (e) {
      setSwapInProgress(false);
    }
  };

  const startFaceSwap = () => {
    console.log(extractedFaces.length);
    setProgress(0);
    setSwapInProgress(true);
    applyEffect();
  };

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setImage: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    setExtractedFaces([]);
    setConvertedImage(null);

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        handleExtractFaces(reader.result as string);
        setImage(reader.result as string);
      }
    };
    if (e?.target?.files?.[0]) {
      reader.readAsDataURL(e.target.files[0]);
      console.log("Uploaded file:", e.target.files[0].name);
    }
  };

  const handleDrop = (
    e: React.DragEvent<HTMLLabelElement>,
    setImage: React.Dispatch<React.SetStateAction<string | null>>,
    setDragOver: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    e.preventDefault();
    setDragOver(false);
    setExtractedFaces([]);
    setConvertedImage(null);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        handleExtractFaces(reader.result as string);
        setImage(reader.result as string);
      }
    };
    if (e?.dataTransfer?.files?.[0]) {
      reader.readAsDataURL(e.dataTransfer.files[0]);
      console.log("Dropped file:", e.dataTransfer.files[0].name);
    }
  };

  const handleImageUploadFromMap = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.target.files?.[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          const newUploadedFaces = [...uploadedFaces];
          newUploadedFaces[index] = reader.result as string;
          setUploadedFaces(newUploadedFaces);
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleDropFromMap = (
    e: React.DragEvent<HTMLLabelElement>,
    index: number
  ) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          const newUploadedFaces = [...uploadedFaces];
          newUploadedFaces[index] = reader.result as string;
          setUploadedFaces(newUploadedFaces);
        }
      };
      reader.readAsDataURL(e.dataTransfer.files[0]);
    }
    setDragOverFaces((prev) => {
      const newArr = [...prev];
      newArr[index] = false;
      return newArr;
    });
  };

  const handleDownload = () => {
    if (!convertedImage) return;
    var imageUid = uuidv4();
    const link = document.createElement("a");
    link.href = convertedImage;
    link.download = `swaptoon_buoyancy_${imageUid}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (!convertedImage) return;

    try {
      if (navigator.share) {
        try {
          await navigator.share({
            files: [
              new File([base64ToBlob(convertedImage)], "shared_image.png", {
                type: "image/png",
              }),
            ],
            title: "Check out this image!",
            text: "I wanted to share this cool image with you.",
          });
          console.log("Image shared successfully");
        } catch (error) {
          console.log("Error sharing the image:", error);
        }
      } else {
        console.log("Web Share API is not supported in your browser.");
      }
    } catch (error) {
      console.log("Error sharing:", error);
    }
  };

  const base64ToBlob = (base64: string): Blob => {
    const byteCharacters = atob(base64.split(",")[1]);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(null)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: "image/png" });
  };

  const handleImageTap = () => {
    if (window.screen.width <= 768) {
      setIsTapped((prev) => !prev);
    }
  };

  return (
    <>
      <ScrollToTop />
      <div className="flex flex-1">
        <div className="hidden lg:block w-1/5 p-4 mt-14">
          {/* Advertise section */}
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-3821089660320367"
            data-ad-slot="7381608051"
            data-ad-format="auto"
            data-full-width-responsive="true"
          ></ins>
        </div>
        <div className="w-full lg:w-3/5 p-4">
          <>
            <div className="d-flex flex-column align-items-center justify-center mb-4 mt-24 text-center hero-ambient-glow">
              <div className="gradient-badge mb-3 px-4 py-2 text-indigo-300 font-semibold text-sm d-inline-flex align-items-center gap-2">
                {badgeIcon} {badgeText}
              </div>
              <h1 className="gradient-text" style={{ fontSize: "2.5rem", fontWeight: 800, letterSpacing: "-0.5px" }}>
                {heroHeading}
              </h1>
            </div>
            <div className="d-flex justify-center mb-5 text-center">
              <div className="glass-panel-hero p-4 p-md-4" style={{ borderRadius: "24px", maxWidth: "900px" }}>
                <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f8fafc", marginBottom: "10px" }}>
                  {heroSubheading}
                </h2>
                <p style={{ color: "#94a3b8", fontSize: "0.95rem", lineHeight: 1.6, margin: 0 }}>
                  {heroText}
                </p>
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="align-items-center col-12 col-lg-8 d-flex justify-content-center">
                <div
                  className="glass-panel-hero p-4 p-md-5 w-100 d-flex flex-column align-items-center mb-4"
                  style={{ borderRadius: "24px" }}
                >
                  <div className="row w-100 justify-content-center g-4">
                    <div
                      className={`align-items-center col-12 d-flex flex-column upload-box-wrapper ${
                        originalImage && "col-md-6"
                      }`}
                    >
                      <label className="font-bold text-lg mb-3 d-flex align-items-center gap-2" style={{ color: "#e2e8f0" }}>
                        <PhotoCamera sx={{ fontSize: 22, color: "#818cf8" }} /> Group Photo (Target)
                      </label>
                      <label
                        htmlFor="originalUpload"
                        className={`p-3 rounded-2xl w-full flex flex-col justify-center items-center cursor-pointer transition-all ${
                          originalImage || dragOverOriginal
                            ? "border-2 border-indigo-400 bg-indigo-950/50 shadow-lg shadow-indigo-500/20"
                            : "border-2 border-dashed border-slate-600 hover:border-indigo-400 bg-slate-800/40 hover:bg-slate-800/70"
                        }`}
                        style={{ height: "220px", width: "100%", maxWidth: "240px", borderRadius: "18px" }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverOriginal(true);
                        }}
                        onDragLeave={() => setDragOverOriginal(false)}
                        onDrop={(e) =>
                          handleDrop(e, setOriginalImage, setDragOverOriginal)
                        }
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="originalUpload"
                          onChange={(e) =>
                            handleImageUpload(e, setOriginalImage)
                          }
                        />
                        <div
                          className="align-items-center cursor-pointer d-flex justify-content-center text-center w-full h-full"
                        >
                          {originalImage ? (
                            <img
                              src={originalImage}
                              alt="Original"
                              className="w-full h-full object-cover"
                              style={{ borderRadius: "12px" }}
                            />
                          ) : (
                            <div className="d-flex flex-column align-items-center gap-2 p-2">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: 54,
                                  height: 54,
                                  background: "rgba(99, 102, 241, 0.15)",
                                  border: "1px solid rgba(165, 180, 252, 0.3)",
                                }}
                              >
                                <PhotoCamera sx={{ fontSize: 32, color: "#a5b4fc" }} />
                              </div>
                              <span style={{ color: "#a5b4fc", fontSize: "0.9rem", fontWeight: 700 }}>
                                Upload Group Photo
                              </span>
                              <span style={{ color: "#64748b", fontSize: "0.75rem" }}>
                                Drag & drop or browse
                              </span>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                    {originalImage && (
                      <div className="align-items-center col-12 col-md-6 d-flex flex-column">
                        <label className="font-bold text-lg mb-3" style={{ color: "#e2e8f0" }}>
                          Detected Faces ({extractedFaces?.length || 0})
                        </label>
                        <div className="d-flex flex-column gap-3 justify-start overflow-auto p-2 rounded-2xl w-full" style={{ maxHeight: "260px", background: "rgba(15, 23, 42, 0.4)", border: "1px solid rgba(255,255,255,0.1)" }}>
                          {extractedFaces?.map((face, index) => (
                            <div
                              key={index}
                              className="d-flex align-items-center justify-content-between p-2 rounded-xl"
                              style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)" }}
                            >
                              <img
                                src={face}
                                alt={`Extracted ${index + 1}`}
                                style={{
                                  height: 48,
                                  width: 48,
                                  borderRadius: "50%",
                                  border: "2px solid #6366f1",
                                  objectFit: "cover",
                                }}
                              />
                              <FaLongArrowAltRight style={{ color: "#818cf8" }} />
                              <label
                                htmlFor={`faceUpload${index}`}
                                className={`cursor-pointer m-0 transition-all ${dragOverFaces[index]
                                  ? "border-2 border-indigo-400 bg-indigo-950/40"
                                  : "border-2 border-dashed border-slate-600 bg-slate-800/30"
                                }`}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  setDragOverFaces((prev) => {
                                    const newArr = [...prev];
                                    newArr[index] = true;
                                    return newArr;
                                  });
                                }}
                                onDragLeave={() =>
                                  setDragOverFaces((prev) => {
                                    const newArr = [...prev];
                                    newArr[index] = false;
                                    return newArr;
                                  })
                                }
                                onDrop={(e) => handleDropFromMap(e, index)}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  id={`faceUpload${index}`}
                                  onChange={(e) =>
                                    handleImageUploadFromMap(e, index)
                                  }
                                />
                                <div
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ width: "48px", height: "48px" }}
                                >
                                  {uploadedFaces[index] ? (
                                    <img
                                      src={uploadedFaces[index]}
                                      alt={`Uploaded ${index + 1}`}
                                      style={{
                                        height: 48,
                                        width: 48,
                                        borderRadius: "50%",
                                        border: "2px solid #d946ef",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        borderRadius: "50%",
                                        border: "2px dashed #a855f7",
                                        background: "rgba(168, 85, 247, 0.15)",
                                        width: 48,
                                        height: 48,
                                        color: "#c084fc",
                                      }}
                                      className="d-flex align-items-center justify-content-center"
                                    >
                                      <FaPlus size={16} />
                                    </div>
                                  )}
                                </div>
                              </label>
                            </div>
                          ))}
                          {extractedFaces?.length === 0 && !convertedImage && (
                            <div className="align-items-center d-flex h-100 justify-content-center w-100 flex-column gap-2 py-4">
                              <CircularProgress size={30} style={{ color: "#818cf8" }} />
                              <span className="font-semibold" style={{ color: "#a5b4fc", fontSize: "0.9rem" }}>
                                AI Detecting Faces...
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {swapInProgress ? (
                    <div className="w-100 mt-4">
                      <CustomProgressBar swapInProgress={swapInProgress} progress={progress} setProgress={setProgress} />
                    </div>
                  ) : (
                    <button
                      className="btn-gradient mt-4 px-5 py-3 text-white disabled:opacity-50 d-flex align-items-center gap-2 border-0 cursor-pointer"
                      disabled={
                        !originalImage ||
                        swapInProgress ||
                        uploadedFaces.length !== extractedFaces.length
                      }
                      onClick={startFaceSwap}
                      style={{ fontSize: "1.1rem" }}
                    >
                      <AutoAwesome sx={{ fontSize: 22 }} /> Start Multiple Face Swapping
                    </button>
                  )}
                </div>
              </div>

              <div className="col-12 col-lg-4 d-flex d-lg-block justify-content-center mt-5 mt-lg-0">
                <div
                  className={`d-flex justify-content-center position-relative ${style.image_container}`}
                >
                  {convertedImage ? (
                    <motion.div
                      className="position-relative"
                      style={{ width: "100%", height: "100%" }}
                      onHoverStart={() => setIsHovered(true)}
                      onHoverEnd={() => setIsHovered(false)}
                      onClick={handleImageTap}
                    >
                      <motion.img
                        src={convertedImage}
                        alt="Swapped"
                        className="max-h-full rounded"
                        style={{ width: "100%", height: "auto" }}
                        initial={{ opacity: 1 }}
                        animate={{ opacity: isHovered || isTapped ? 0.5 : 1 }}
                      />

                      {(isHovered || isTapped) && (
                        <div
                          className="position-absolute top-0 left-0 w-100 h-100"
                          style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            borderRadius: "inherit",
                            zIndex: 1,
                          }}
                        ></div>
                      )}
                      {(isHovered || isTapped) && (
                        <div
                          className="position-absolute top-50 start-50 translate-middle d-flex gap-3"
                          style={{ pointerEvents: "none", zIndex: 2 }}
                        >
                          <motion.button
                            className="btn btn-primary rounded-circle p-2 d-flex align-items-center justify-content-center border-0"
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={handleDownload}
                            style={{ pointerEvents: "auto", width: "40px", height: "40px" }}
                          >
                            <FaDownload size={20} />
                          </motion.button>

                          <motion.button
                            className="btn btn-success rounded-circle p-2 d-flex align-items-center justify-content-center border-0"
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={handleShare}
                            style={{ pointerEvents: "auto", width: "40px", height: "40px" }}
                          >
                            <FaShareAlt size={20} />
                          </motion.button>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <img
                      src={initialSwappedImage}
                      alt="face_swap_image"
                      style={{ width: "100%", height: "100%" }}
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        </div>
        <div className="hidden lg:block w-1/5 p-4 mt-14">
          {/* Advertise section */}
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-3821089660320367"
            data-ad-slot="7381608051"
            data-ad-format="auto"
            data-full-width-responsive="true"
          ></ins>
        </div>
      </div>
      <div>
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-3821089660320367"
          data-ad-slot="7635816975"
          data-ad-format="auto"
          data-full-width-responsive="true"
        ></ins>
      </div>
      <div>
        <HeroSectionComponent />
      </div>
      <div>
        <FeaturesComponent />
      </div>
      <div>
        <GuideComponent />
      </div>
      <div>
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-3821089660320367"
          data-ad-slot="7635816975"
          data-ad-format="auto"
          data-full-width-responsive="true"
        ></ins>
      </div>
      <div>
        <WhoCanUseComponent />
      </div>
      <div>
        <DownloadDetailSection />
      </div>
      <div>
        <BenefitsComponent />
      </div>
      <div>
        <Review />
      </div>
      <div>
        <InstallSection />
      </div>
      <div className={style.faq_section}>
        <FAQComponent />
      </div>
      <div>
        <Footer />
      </div>
    </>
  );
};

export default MultiFaceSwapTemplate;
